import { Component, OnInit, OnDestroy } from '@angular/core';
import { ModalService } from '@app/shared/modals/services/modal.service';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { ResourceMatcherFormControl, ResourceMatcherColumn, ResourceMatcherPair, ResourceMatcherResource } from './resource-matcher.control';
import { ResourceMatcherModalComponent } from '@app/shared/modals/resource-matcher-modal/resource-matcher-modal.component';
import { BsAppLaunchFormService } from '../../services/bs-app-launch-form.service';
import { SearchResourceType } from '@app/core/model/search/search-resource-type';
import { IResourceMatcherModalInput, IResourceMatcherModalOutput } from '@app/shared/modals/model/action-modal';
import { SearchQueryService } from '@app/core/services/search/resource-search/search-query.service';
import { BsApiEndPoints } from '@app/core/services/bs-api/endpoints';
import { IAppResult } from '@app/core/model/appresults/appresult';
import { SearchResourceDictionaryService } from '@app/core/services/resource-dictionary/search-resource-dictionary.service';
import { ISearchResultItem } from '@app/core/model/search/search-result-item';
import IResource from '@app/core/model/resource';

@Component({
  selector: 'app-bs-app-launch-resource-matcher-control',
  templateUrl: './bs-app-launch-resource-matcher-control.component.html',
  styleUrls: ['./bs-app-launch-resource-matcher-control.component.scss']
})
export class BsAppLaunchResourceMatcherControlComponent implements OnInit, OnDestroy {
  formControl: ResourceMatcherFormControl;

  pairs$: BehaviorSubject<ResourceMatcherPair[]> = new BehaviorSubject([]);
  resourceType: SearchResourceType;
  
  private subs = new SubSink();

  constructor(
    private modalService: ModalService,
    private bsAppLaunchFormService: BsAppLaunchFormService,
    private searchQueryService: SearchQueryService,
    private searchResourceDictionaryService: SearchResourceDictionaryService,
  ) {}

  get buttonText(): string {
    return `${this.formControl.buttonText}...`;
  }

  get hasSelectedPairs(): boolean {
    const pairs = this.pairs$.value;
    return pairs && pairs.length > 0 && pairs.some(pair => 
      Object.values(pair.columns).some(resource => resource !== null)
    );
  }

  ngOnInit(): void {
    this.resourceType = this.formControl.resourceType;

    // Initialize pairs from form control columns
    if (this.formControl.value && this.formControl.value.length > 0) {
      this.updatePairsFromColumns(this.formControl.value);
    }
  }

  getResourceUrl(resource: ResourceMatcherResource): string {
    switch (this.resourceType) {
      case SearchResourceType.Sample:
        return BsApiEndPoints.getSampleDetailsRoute(String(resource.id), resource.name);
      case SearchResourceType.BiosampleLibraryPrep:
        return BsApiEndPoints.getBiosampleDetailsRoute(String(resource.id).split('::')[0]);
      default:
        return null;
    }
  }

  get baseExtendedQuery(): string {
    switch(this.resourceType) {
      case SearchResourceType.Biosample:
      case SearchResourceType.BiosampleLibraryPrep:
        return 'HasQcPassedFastqDatasets:true';
      default:
        return '';
    }
  }

  get tableId(): string {
    return `${this.resourceType}AppLaunchResourceMatcher`;
  }

  openResourceMatcher(): void {

    const extendedBaseQuery = (this.baseExtendedQuery || this.formControl.searchQuery) ? this.searchQueryService.buildQueryString(
      this.baseExtendedQuery,
      this.formControl.searchQuery,
      [],
      null
    ) : null;

    const modalInput: IResourceMatcherModalInput = {
      resourceType: this.resourceType,
      searchDataSourceOptions: {
        useStrictPaging: true,

        // Combining searchQuery and extendedBaseQuery. This is sort of a hack when we need to add additions
        // to the baseQuery but only for the resource chooser in the context of the app launch form. A good example of this is 
        // for the biosample chooser where on the app launch page, we always want to search for biosamples that have
        // FastqDatasets but we don't want this everywhere on the basespace site. On file uploader we give user
        // ability to create a biosample so they can associate a new FASTQ dataset with the biosample so wouldn't want this param there.
        extendedBaseQuery: extendedBaseQuery
      },
      columns: this.formControl.value.map(col => ({
        id: col.id,
        label: col.label,
        resources: col.resources || []
      })) as any,
      maxRows: this.formControl.maxRows,
      allowUnpairedCells: this.formControl.allowUnpairedCells,
      allowRepetition: this.formControl.allowRepetition,
      currentUserId: this.bsAppLaunchFormService.currentUser.Id,
      tableId: this.tableId
    };
    
    const modalEvents = this.modalService.openModal(modalInput, ResourceMatcherModalComponent);
    
    this.subs.sink = modalEvents.confirm.pipe(
      filter((output) => !!output)
    ).subscribe((output: IResourceMatcherModalOutput) => {
      // Update form control with new columns data
      this.formControl.setValue(output.columns as any);
      this.updatePairsFromColumns(this.formControl.value);
      this.modalService.handleError(null);
    });
  }

  removeResource(columnIndex: number, rowIndex: number): void {
    const currentValue = [...this.formControl.value];

    currentValue[columnIndex].resources[rowIndex] = null;
    
    // Check if all resources at this rowIndex are null across all columns
    const isRowEmpty = currentValue.every(column => 
      !column.resources[rowIndex] || column.resources[rowIndex] === null
    );
    
    // If the row is completely empty, remove that index from all columns
    if (isRowEmpty) {
      currentValue.forEach(column => {
        column.resources.splice(rowIndex, 1);
      });
    }

    this.formControl.setValue(currentValue);
    this.updatePairsFromColumns(this.formControl.value);
  }

  getResourceDisplayText(pair: ResourceMatcherPair, columnIndex: number): string {
    const columnId = this.formControl.value[columnIndex].id;
    const resource = pair.columns[columnId];

    if(this.resourceType === SearchResourceType.BiosampleLibraryPrep && resource) {
      return resource.name.replace('::', ': ');
    }

    return resource ? resource.name : '';
  }

  private updatePairsFromColumns(columns: ResourceMatcherColumn[]): void {
    // Find the maximum number of resources across all columns
    const maxLength = Math.max(...columns.map(col => col.resources ? col.resources.length : 0));
    
    const pairs: ResourceMatcherPair[] = [];
    for (let i = 0; i < maxLength; i++) {
      const pairColumns: { [columnId: string]: any } = {};
      
      columns.forEach(column => {
        pairColumns[column.id] = column.resources && column.resources[i] 
          ? column.resources[i] 
          : null;
      });
      
      pairs.push({
        rowIndex: i,
        columns: pairColumns
      });
    }
    
    this.pairs$.next(pairs);
  }

  destroy(): void {
    this.formControl.destroy();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}

